// What's New on My Desk (owner, 2026-10-06: "puts a [card] that disappears
// once read but also puts it in communications ... a button for Mark Read
// which removes it from desk but leaves it in Communications"; "I need to be
// able to see the what's new on my user and have a publish button ... till
// such time as I hit publish it doesn't post to their communications or
// Desk").
//
// A What's New is a communication flagged `whats_new` (095). Until the owner
// publishes it (096) it is a DRAFT the server sends to nobody but him: on his
// Desk it shows with Edit and Publish. Once published, everyone sees it in
// Communications, and here — above the Desk grid — until they press Mark
// read (the same per-person read record Communications already keeps).
import { followInAppLink } from '../lib/router';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { refetchResource, useResource } from '../lib/store';
import { session } from '../lib/session';
import {
  COMMUNICATIONS_KEY, fetchCommunications, fetchCommunicationDetail, markCommunicationRead,
  createWhatsNewDraft, updateWhatsNewDraft, publishWhatsNew,
  type CommunicationsData, type CommunicationListItem,
} from '../lib/communications';
import { formatClinicDateTime } from '../lib/reportSchedules';

/** What this person's Desk shows: the owner's drafts, then published ones not
 * yet read, newest first. Pure. */
export function whatsNewForDesk(data: CommunicationsData | null): CommunicationListItem[] {
  if (!data) return [];
  return data.communications.filter((c) => c.whatsNew && (c.draft || c.unread));
}

const refresh = (): Promise<void> => refetchResource(COMMUNICATIONS_KEY, fetchCommunications);

const HOW_TO_WRITE = 'Write plain text. A line starting "# " is a heading, lines starting "- " are bullets, '
  + '**words** are bold, and a blank line starts a new paragraph.';

function Editor({
  id, subject, bodyText, onDone,
}: { id: number | null; subject: string; bodyText: string; onDone: () => void }): JSX.Element {
  const [title, setTitle] = useState(subject);
  const [text, setText] = useState(bodyText);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (id === null) await createWhatsNewDraft(title, text);
      else await updateWhatsNewDraft(id, title, text);
      await refresh();
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="new-hire-block" data-testid="whats-new-editor">
      <label class="nh-field">
        <span class="nh-label">Title</span>
        <input type="text" value={title} data-testid="whats-new-title" onInput={(e) => setTitle((e.target as HTMLInputElement).value)} />
      </label>
      <label class="nh-field">
        <span class="nh-label">Message</span>
        <textarea class="notes-entry" rows={14} value={text} data-testid="whats-new-text" onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} />
      </label>
      <p class="muted">{HOW_TO_WRITE}</p>
      {error ? <p class="warn" role="alert">{error}</p> : null}
      <div class="actions">
        <button type="button" class="btn primary" disabled={busy || !title.trim()} onClick={() => { void save(); }} data-testid="whats-new-save">
          {busy ? 'Saving…' : 'Save draft'}
        </button>
        <button type="button" class="btn" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

function OneCard({ c, isOwner }: { c: CommunicationListItem; isOwner: boolean }): JSX.Element {
  const [open, setOpen] = useState(Boolean(c.draft));
  const [detail, setDetail] = useState<{ html: string; text: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  async function load(): Promise<{ html: string; text: string }> {
    const d = await fetchCommunicationDetail(c.id);
    const v = { html: d.communication.bodyHtml || '', text: d.communication.bodyText || '' };
    setDetail(v);
    setLoaded(true);
    return v;
  }
  useEffect(() => {
    if (open && !loaded) void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [open, loaded]);

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="card whats-new-card" data-testid={`whats-new-${c.id}`}>
      <div class="section-head-row wn-band">
        <div>
          <div class="wn-kicker">nvManager · What's New</div>
          <h3>
            {c.subject || "What's new in nvManager"}
            {c.draft ? <span class="badge warn" data-testid={`whats-new-draft-${c.id}`}> Draft — only you can see this</span> : null}
          </h3>
        </div>
        <div class="actions">
          {c.draft && isOwner ? (
            <>
              <button type="button" class="btn" onClick={() => { setEditing(!editing); setOpen(true); }} data-testid={`whats-new-edit-${c.id}`}>
                {editing ? 'Close editor' : 'Edit'}
              </button>
              <button
                type="button" class="btn primary" disabled={busy || editing}
                onClick={() => { void act(() => publishWhatsNew(c.id)); }}
                data-testid={`whats-new-publish-${c.id}`}
              >
                {busy ? 'Publishing…' : 'Publish to everyone'}
              </button>
            </>
          ) : (
            <>
              <button type="button" class="btn" onClick={() => setOpen(!open)} data-testid={`whats-new-read-${c.id}`}>
                {open ? 'Close' : 'Read it'}
              </button>
              <button
                type="button" class="btn primary" disabled={busy}
                onClick={() => { void act(() => markCommunicationRead(c.id)); }}
                data-testid={`whats-new-mark-${c.id}`}
              >
                {busy ? 'Saving…' : 'Mark read'}
              </button>
            </>
          )}
        </div>
      </div>
      <p class="muted">
        {c.draft
          ? 'Nobody else sees this until you press Publish. Then it goes on everyone\'s Desk and into Communications.'
          : `${c.receivedAt ? formatClinicDateTime(c.receivedAt) : ''} · stays in Communications after you mark it read`}
      </p>
      {error ? <p class="warn" role="alert">{error}</p> : null}
      {editing && detail ? (
        <Editor id={c.id} subject={c.subject || ''} bodyText={detail.text} onDone={() => { setEditing(false); setLoaded(false); }} />
      ) : null}
      {open && !editing && detail ? (
        // Built server-side from escaped plain text (src/whats_new.js) — the
        // same sanitized-body rendering every communication uses.
        // eslint-disable-next-line react/no-danger
        <div class="communication-body" data-testid={`whats-new-body-${c.id}`} onClick={followInAppLink} dangerouslySetInnerHTML={{ __html: detail.html }} />
      ) : null}
    </div>
  );
}

export function WhatsNewCards(): JSX.Element | null {
  const load = useResource<CommunicationsData>(COMMUNICATIONS_KEY, fetchCommunications);
  const me = session.value.me;
  const isOwner = me?.role === 'OWNER' && !me.viewingAs;
  const [writing, setWriting] = useState(false);
  const items = whatsNewForDesk(load.status === 'ready' ? load.data : null);
  if (!items.length && !isOwner) return null;
  return (
    <div data-testid="whats-new">
      {items.map((c) => <OneCard key={c.id} c={c} isOwner={isOwner} />)}
      {isOwner ? (
        writing
          ? <div class="card"><Editor id={null} subject="What's new in nvManager" bodyText="" onDone={() => setWriting(false)} /></div>
          : (
            <div class="actions">
              <button type="button" class="btn" onClick={() => setWriting(true)} data-testid="whats-new-write">
                Write a What's New
              </button>
            </div>
          )
      ) : null}
    </div>
  );
}
